// Admin interface for managing rubrics (create, edit, delete, set active)
// This component provides a view of rubrics organized by fair

import { useCallback, useEffect, useState } from 'react';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import {
  getRubricsByFair,
  setRubricAsActive,
  setRubricAsInactive,
  deleteRubricById,
} from '../../../services/rubric.service';
import { getFairs } from '../../../services/fairs.services';
import { usersService } from '../../../services/users.service';
import { getRegionById } from '../../../services/regions.services';
import type { Rubric } from '../../../types/rubric.types';
import type { Fair } from '../../../types/fair.types';
import { useAuth } from '../../../contexts/AuthContext';
import RubricEditorDialog from './RubricEditorDialog';
import { Alert, CircularProgress, Chip, Box } from '@mui/material';

/**
 * RubricManagement - Main admin interface for rubric management
 * Displays rubrics organized by fair with max 3 active per fair
 */
export default function RubricManagment() {
  const { authStatus } = useAuth();
  const [fairs, setFairs] = useState<Fair[]>([]);
  const [rubricsByFair, setRubricsByFair] = useState<Record<string, Rubric[]>>(
    {}
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Dialog states
  const [open, setOpen] = useState(false);
  const [selectedFairId, setSelectedFairId] = useState<string | null>(null);
  const [editingRubric, setEditingRubric] = useState<Rubric | null>(null);

  // Fetch admin's region and fairs
  const fetchFairs = useCallback(async () => {
    try {
      setLoading(true);
      const userId = authStatus.user?.uid;
      if (!userId) {
        setError('User information not available');
        return;
      }

      const userRegionId = await usersService.getUserRegion(userId);
      const userRegion = await getRegionById(userRegionId);

      if (!userRegion) {
        setError('You are not associated with any region');
        return;
      }

      const fairsList = await getFairs(userRegion.id);
      setFairs(fairsList);

      // Fetch rubrics for each fair
      const rubrics: Record<string, Rubric[]> = {};
      for (const fair of fairsList) {
        rubrics[fair.id] = await getRubricsByFair(fair.id);
      }
      setRubricsByFair(rubrics);
    } catch (err) {
      console.error('Error fetching fairs:', err);
      setError('Failed to load fairs');
    } finally {
      setLoading(false);
    }
  }, [authStatus.user?.uid]);

  useEffect(() => {
    fetchFairs();
  }, [fetchFairs]);

  const handleActivateRubric = async (rubricId: string, fairId: string) => {
    try {
      await setRubricAsActive(rubricId);

      // Update local state instead of refetching
      setRubricsByFair((prev) => ({
        ...prev,
        [fairId]: prev[fairId].map((r) =>
          r.id === rubricId ? { ...r, isActive: true } : r
        ),
      }));
    } catch (err: any) {
      alert(err.message || 'Failed to activate rubric');
    }
  };

  const handleDeactivateRubric = async (rubricId: string, fairId: string) => {
    try {
      await setRubricAsInactive(rubricId);

      // Update local state instead of refetching
      setRubricsByFair((prev) => ({
        ...prev,
        [fairId]: prev[fairId].map((r) =>
          r.id === rubricId ? { ...r, isActive: false } : r
        ),
      }));
    } catch (err: any) {
      alert(err.message || 'Failed to deactivate rubric');
    }
  };

  const handleDeleteRubric = async (rubric: Rubric) => {
    if (rubric.isActive) {
      alert('Cannot delete an active rubric. Please deactivate it first.');
      return;
    }
    if (!window.confirm(`Delete rubric "${rubric.name}"?`)) return;

    try {
      await deleteRubricById(rubric.id);

      // Update local state instead of refetching
      setRubricsByFair((prev) => ({
        ...prev,
        [rubric.fairId]: prev[rubric.fairId].filter((r) => r.id !== rubric.id),
      }));
    } catch (err) {
      alert('Failed to delete rubric');
    }
  };

  const getActiveCount = (fairId: string) => {
    return rubricsByFair[fairId]?.filter((r) => r.isActive).length || 0;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold">Rubric Management</h2>
        <p className="text-sm text-gray-600">
          Manage rubrics for each fair (maximum 3 active per fair)
        </p>
      </div>

      {fairs.length === 0 ? (
        <Alert severity="info">
          No fairs available. Create a fair first to add rubrics.
        </Alert>
      ) : (
        fairs.map((fair) => {
          const fairRubrics = rubricsByFair[fair.id] || [];
          const activeCount = getActiveCount(fair.id);

          return (
            <Card key={fair.id} className="p-6">
              {/* Fair Header */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-xl font-semibold">{fair.name}</h3>
                  <p className="text-sm text-gray-600">
                    {fairRubrics.length} / 3 rubrics • {activeCount} active
                  </p>
                </div>
                <Button
                  onClick={() => {
                    setSelectedFairId(fair.id);
                    setOpen(true);
                  }}
                  disabled={fairRubrics.length >= 3}
                >
                  New Rubric
                </Button>
              </div>

              {/* Rubrics List */}
              {fairRubrics.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No rubrics created for this fair yet
                </div>
              ) : (
                <div className="space-y-2">
                  {fairRubrics.map((rubric) => (
                    <div
                      key={rubric.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-3">
                        <div>
                          <div className="font-medium">{rubric.name}</div>
                          <div className="text-sm text-gray-500">
                            {rubric.totalPoints} points •{' '}
                            {rubric.questions.length} questions
                          </div>
                        </div>
                        {rubric.isActive && (
                          <Chip label="Active" color="success" size="small" />
                        )}
                      </div>

                      <div className="flex gap-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setEditingRubric(rubric)}
                        >
                          Edit
                        </Button>

                        {rubric.isActive ? (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() =>
                              handleDeactivateRubric(rubric.id, fair.id)
                            }
                          >
                            Deactivate
                          </Button>
                        ) : (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() =>
                              handleActivateRubric(rubric.id, fair.id)
                            }
                            disabled={activeCount >= 3}
                          >
                            Activate
                          </Button>
                        )}

                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleDeleteRubric(rubric)}
                          disabled={rubric.isActive}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          );
        })
      )}

      {/* Rubric Editor Dialog */}
      <RubricEditorDialog
        open={open || Boolean(editingRubric)}
        initialRubric={editingRubric || undefined}
        fairId={editingRubric?.fairId || selectedFairId || undefined}
        onClose={() => {
          setOpen(false);
          setEditingRubric(null);
          setSelectedFairId(null);
        }}
        onSaved={async (savedRubric?: Rubric) => {
          if (savedRubric && selectedFairId && !editingRubric) {
            // Add newly created rubric to local state
            setRubricsByFair((prev) => ({
              ...prev,
              [selectedFairId]: [...(prev[selectedFairId] || []), savedRubric],
            }));
          } else if (editingRubric && savedRubric) {
            // Update edited rubric in local state
            const fairId = editingRubric.fairId;
            setRubricsByFair((prev) => ({
              ...prev,
              [fairId]: prev[fairId].map((r) =>
                r.id === editingRubric.id ? savedRubric : r
              ),
            }));
          }

          setOpen(false);
          setEditingRubric(null);
          setSelectedFairId(null);
        }}
      />
    </div>
  );
}
